import styles from './Button.module.scss';

import cx from 'classnames';
import React, { ComponentProps, forwardRef } from 'react';

import {
  ButtonTheme,
  HasButtonOutline,
  HasButtonTheme,
  HasCustomIcon,
  HasCustomIconPosition,
  HasImage,
  HasLinkVariant,
  HasTitle,
  Link,
  LinkVariant,
} from 'api/contentful/contentTypes.cloverdotcom';
import { ContentImage } from 'components/ContentImage/ContentImage';
import { ContentLinkV2, IContentLinkV2Props } from 'components/ContentLinkV2/ContentLinkV2';
import { TextWithArrow } from 'components/TextWithArrow/TextWithArrow';
import type { FlatEntry } from 'services/cms/flattenContentful';

export interface IButtonLinkV2Props<
  T extends FlatEntry<Link> = FlatEntry<
    HasLinkVariant &
      HasImage &
      HasButtonTheme &
      HasButtonOutline &
      HasTitle &
      HasCustomIcon &
      HasCustomIconPosition
  >,
> extends Omit<IContentLinkV2Props, 'entry' | 'size'> {
  entry?: T;
  imageClassName?: string;
  isFullWidth?: boolean;
  size?: ComponentProps<typeof TextWithArrow>['size'];
  isButton?: boolean;
}

export const ButtonLinkV2 = forwardRef<HTMLAnchorElement, IButtonLinkV2Props>(
  ({ className, imageClassName, isFullWidth, isButton, children, size, ...props }, ref) => {
    const { variant, image, title, customIcon, customIconPosition } = props.entry ?? {};
    const hasImage = !!image;

    let buttonTheme = props.entry?.buttonTheme;
    let buttonOutline = props.entry?.buttonOutline;

    if (!buttonTheme && variant === LinkVariant.PRIMARY) {
      buttonTheme = ButtonTheme.LIGHT;
    } else if (
      (!buttonTheme && variant === LinkVariant.SECONDARY) ||
      (!buttonTheme && !variant && isButton)
    ) {
      buttonTheme = ButtonTheme.LIGHT;
      buttonOutline = true;
    }

    const linkClassName = cx(
      {
        [styles.buttonFullWidth]: isFullWidth,
        [styles.buttonImage]: hasImage,
      },
      !buttonTheme && {
        [styles.buttonVariantPrimary]: variant === LinkVariant.PRIMARY,
        [styles.buttonVariantSecondary]: variant === LinkVariant.SECONDARY,
        [styles.buttonVariantText]: variant === LinkVariant.TEXT,
        [styles.button]: true,
      },
      buttonTheme && {
        [styles.withButtonTheme]: true,
        [styles[buttonTheme]]: true,
        [styles.outline]: buttonOutline,
      },
      className,
    );

    const checkAriaDisabled = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (event.currentTarget.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        return;
      }

      props.onClick?.(event);
    };

    return (
      <ContentLinkV2
        {...props}
        className={linkClassName}
        ref={ref}
        hideTitle={hasImage || !!buttonTheme}
        onClick={checkAriaDisabled}
      >
        {image && <ContentImage entry={image} className={imageClassName} />}
        {buttonTheme ? (
          <TextWithArrow
            cta
            theme={buttonTheme}
            outline={buttonOutline}
            className={styles.textWithArrow}
            size={size}
            customIcon={customIcon?.file?.url}
            customIconPosition={customIconPosition}
          >
            {children ?? title}
          </TextWithArrow>
        ) : (
          children
        )}
      </ContentLinkV2>
    );
  },
);

ButtonLinkV2.displayName = 'ButtonLinkV2';
